/**
 * Copy for the **claim** details drawer.
 *
 * Its own file rather than more keys under `orders.orderDetails`: a claim is an order of type Claim
 * on the wire, but it is not one to a reader, and reaching into the order namespace for claim
 * wording is what the split is undoing. The order drawer's twin is `orders/order-drawer.ts`;
 * strings that happen to read the same are written out in both rather than interpolated from a
 * `{type}` the caller passes in.
 *
 * `deductions` is the half with no twin at all — retentions and contra charges are the claim's own.
 */
export default {
  inventory: {
    title: 'Claim items',
    headers: {
      itemName: 'Item name',
      specification: 'Spec',
      boq: 'BOQ section',
      previous: 'Previous',
      qty: 'Qty',
      rate: 'Rate',
      total: 'Total',
      actions: 'Actions',
    },
    headerTooltips: {
      previous: 'Quantity already taken against this BOQ line before this claim.',
      qty: 'Quantity on this claim.',
    },
    variationCause: {
      label: 'Variation · {reasons}',
      reasonSeparator: ', ',
      newLineItem: 'new line item',
      quantityUp: 'quantity increased',
      quantityDown: 'quantity decreased',
      rateUp: 'rate increased',
      rateDown: 'rate decreased',
      valueChanged: 'Variation · value changed',
    },
    itemRemoved: 'This item has been removed from the claim',
  },

  summary: {
    title: 'Summary',
    // The parts, then what they come to. Named for the claim rather than built from a `{type}`,
    // like the rest of this file.
    claimItems: 'Claim items',
    deductions: 'Deductions',
    inventoryTotal: 'Inventory total',
    deliveredTotal: 'Delivered total',
    claimTotal: 'Claim Total',
    vatTotal: 'VAT Total',
    pendingVariationsTotal: 'Pending variations total',
    totalInclVat: 'Total Incl VAT',
    vatNote: 'VAT registered vendors will have their VAT % added to their total price',
    negativeTotalNote:
      'This claim totals less than zero because the deductions against it exceed what is being claimed.',
  },

  deductions: {
    title: 'Deductions',
    claimItemsTotal: 'Claim Total',

    retentions: {
      title: 'Retentions',
      /** What a row is, where a BOQ line would have its name. */
      rowLabel: 'Retention',
      add: 'Add Retention amount',
      total: 'Retentions Total',
      emptyTitle: 'No retentions on this claim',
      remove: 'Remove retention',
      assigneePlaceholder: 'Assignee',
      dueDatePlaceholder: 'Due date',
      headers: {
        itemName: 'Type',
        assignee: 'Assigned to',
        dueDate: 'Due date',
        previous: 'Previous',
        percent: 'Qty',
        total: 'Total',
      },
      headerTooltips: {
        previous: 'Total already requested from this vendor, across every claim before this one.',
      },
    },

    itemContraCharges: {
      title: 'Item Contra Charges',
      add: 'Add Item Contra Charge',
      total: 'Item Contra Charges Total',
      emptyTitle: 'No item contra charges on this claim',
      remove: 'Remove the contra charge on {name}',
      headers: {
        itemName: 'Item name',
        specification: 'Spec',
        boq: 'BOQ section',
        qty: 'Qty',
        rate: 'Rate',
        total: 'Total',
      },
    },

    valueContraCharges: {
      title: 'Value Contra Charges',
      rowLabel: 'Value Contra Charge',
      add: 'Add Value Contra Charge',
      total: 'Value Contra Charges Total',
      emptyTitle: 'No value contra charges on this claim',
      remove: 'Remove value contra charge',
      headers: {
        itemName: 'Type',
        amount: 'Amount',
      },
    },
  },
};
